import React, { useEffect, useCallback } from 'react';
import { FaTimes, FaStar, FaImdb, FaExternalLinkAlt } from 'react-icons/fa';

function TrailerOverlay({ selectedItem, trailerURL, closeTrailerOverlay }) {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeTrailerOverlay();
    }
  }, [closeTrailerOverlay]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  const activeUrl = trailerURL || (selectedItem && selectedItem.trailer ? (
    selectedItem.trailer.includes('http') 
      ? selectedItem.trailer 
      : `https://www.youtube.com/watch?v=${selectedItem.trailer}`
  ) : null);

  if (!activeUrl && !selectedItem) return null;

  // Extract YouTube video ID
  let videoId = '';
  if (activeUrl) {
    if (activeUrl.includes('v=')) {
      videoId = activeUrl.split('v=')[1]?.split('&')[0];
    } else if (activeUrl.includes('youtu.be/')) {
      videoId = activeUrl.split('youtu.be/')[1]?.split('?')[0];
    } else if (activeUrl.includes('embed/')) {
      videoId = activeUrl.split('embed/')[1]?.split('?')[0];
    } else {
      videoId = activeUrl;
    }
  }

  const title = selectedItem?.title || selectedItem?.name || 'Trailer Preview';
  const releaseDate = selectedItem?.release_date || selectedItem?.first_air_date;
  const releaseYear = releaseDate ? releaseDate.slice(0, 4) : '';
  const rating = selectedItem?.vote_average ? Number(selectedItem.vote_average).toFixed(1) : null;
  const overview = selectedItem?.overview;
  const streamingProviders = selectedItem?.streamingProviders || [];

  return (
    <div className="cinema-modal-overlay" onClick={closeTrailerOverlay}>
      <div
        className="cinema-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button
          className="cinema-modal-close-btn"
          onClick={closeTrailerOverlay}
          aria-label="Close modal"
        >
          <FaTimes />
        </button>

        {videoId ? (
          <div className="cinema-modal-player">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
              title={`${title} Trailer Player`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="cinema-modal-iframe"
            />
          </div>
        ) : null}

        {selectedItem && (
          <div className="cinema-modal-info">
            <div className="modal-info-header">
              <div>
                <h3 className="modal-title">{title}</h3>
                <div className="modal-badges-row" style={{ marginTop: '8px' }}>
                  {releaseYear && <span className="badge-year">{releaseYear}</span>}
                  {rating && (
                    <span className={Number(rating) >= 7 ? 'rating-pill-green' : 'rating-pill-neutral'}>
                      <FaStar style={{ fontSize: '11px' }} />
                      {rating} TMDB
                    </span>
                  )}
                </div>
              </div>

              <div className="modal-links">
                {selectedItem.imdbURL && (
                  <a
                    href={selectedItem.imdbURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-external-link"
                  >
                    <FaImdb style={{ fontSize: '16px', color: '#f5c518' }} /> IMDb
                  </a>
                )}
                {selectedItem.tmdbURL && (
                  <a
                    href={selectedItem.tmdbURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="modal-external-link"
                  >
                    <FaExternalLinkAlt style={{ fontSize: '11px' }} /> TMDB
                  </a>
                )}
              </div>
            </div>

            {overview && (
              <p className="modal-overview">{overview}</p>
            )}

            {streamingProviders.length > 0 && (
              <div className="modal-streaming-section">
                <span className="modal-streaming-label">Streaming in NL:</span>
                <div className="streaming-logos-wrap">
                  {streamingProviders.map((provider) => (
                    <img
                      key={provider.provider_id}
                      src={`https://image.tmdb.org/t/p/original${provider.logo_path}`}
                      alt={provider.provider_name}
                      title={provider.provider_name}
                      className="provider-logo-img"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrailerOverlay;